import { useEffect, useState } from 'react';
import { MdNotificationsActive, MdNotificationsOff } from 'react-icons/md';
import { leggiNotifiche, iscriviDispositivo, notificaDiProva, salvaPreferenze, togliDispositivo } from '../api/notifiche';
import { attiva, disattiva, iPhoneDaSafari, iscrizioneAttuale, nomeDispositivo, permesso, supportate } from '../mobile/notifiche';

// Notifiche sul telefono (richiesta del 04/10/2026): messaggi ed eventi di
// Wozzap, ciascuno con il suo interruttore; le singole chat si silenziano dalla
// chat (Dettagli). Si possono anche spegnere da Impostazioni → Notifiche di iOS.
const VOCI = [
  ['messaggi', 'Messaggi', 'Un messaggio nuovo in una chat non silenziata. Mentre Wozzap è aperta non suona, come WhatsApp.'],
  ['eventi', 'Eventi di Wozzap', 'Risposte delle regole, promemoria, @cal, avvisi d’errore.'],
  ['anteprima', 'Anteprima del testo', 'Il testo del messaggio nella notifica (anche sulla schermata bloccata).'],
  ['scollegamento', 'WhatsApp scollegato', 'Se l’account resta scollegato per qualche minuto.'],
];

export default function NotifichePage() {
  const [stato, setStato] = useState(null);
  const [qui, setQui] = useState(null);           // iscrizione di questo dispositivo
  const [occupato, setOccupato] = useState(false);
  const [avviso, setAvviso] = useState('');

  useEffect(() => {
    leggiNotifiche().then(({ data }) => setStato(data)).catch(() => setAvviso('Impostazioni delle notifiche non lette. Riprova.'));
    iscrizioneAttuale().then(setQui).catch(() => setQui(null));
  }, []);

  const quiAttiva = !!qui && !!stato?.dispositivi?.some(d => d.endpoint === qui.endpoint);

  const accendi = async () => {
    setOccupato(true); setAvviso('');
    try {
      const iscrizione = await attiva(stato.chiave_pubblica);
      const { data } = await iscriviDispositivo(iscrizione, nomeDispositivo());
      setStato(data);
      setQui(await iscrizioneAttuale());
      setAvviso('Notifiche attive su questo dispositivo.');
    } catch (err) {
      setAvviso(err.codice === 'denied'
        ? 'Le notifiche sono bloccate: riattivale da Impostazioni → Notifiche → Wozzap, poi riprova.'
        : 'Attivazione non riuscita. Riprova.');
    } finally { setOccupato(false); }
  };
  const spegni = async () => {
    setOccupato(true); setAvviso('');
    try {
      const endpoint = await disattiva();
      if (endpoint) setStato((await togliDispositivo(endpoint)).data);
      setQui(null);
    } catch (_) { setAvviso('Disattivazione non riuscita. Riprova.'); }
    finally { setOccupato(false); }
  };
  const cambia = async (campo, valore) => {
    setStato(s => ({ ...s, preferenze: { ...s.preferenze, [campo]: valore } }));
    try { setStato((await salvaPreferenze({ [campo]: valore })).data); }
    catch (_) { setAvviso('Scelta non salvata. Riprova.'); }
  };
  const prova = async () => {
    setAvviso('');
    try {
      const { data } = await notificaDiProva();
      setAvviso(data.consegnate ? 'Notifica di prova inviata: dovrebbe arrivare fra un attimo.' : 'Nessun dispositivo l’ha ricevuta.');
    } catch (err) { setAvviso(err.response?.data?.error || 'Prova non riuscita.'); }
  };

  return <div className="mobile-page notifiche-page">
    <div className="page-eyebrow">PREFERENZE</div><h1>Notifiche.</h1>
    <p className="page-intro">Messaggi ed eventi di Wozzap sul telefono, anche con l’app chiusa.</p>

    <section className="notifiche-dispositivo" aria-live="polite">
      {!supportate() ? (
        iPhoneDaSafari()
          ? <p><strong>Su iPhone serve Wozzap sulla schermata Home.</strong> In Safari tocca Condividi → «Aggiungi alla schermata Home», poi apri Wozzap da lì e torna in questa pagina.</p>
          : <p>Questo browser non riceve notifiche.</p>
      ) : !stato ? <p>Caricamento…</p> : !stato.configurate ? <p>Le notifiche non sono ancora configurate sul server.</p> : quiAttiva ? (
        <>
          <p className="notifiche-stato attivo"><MdNotificationsActive size={22} aria-hidden="true" /> Attive su questo dispositivo</p>
          <div className="notifiche-azioni">
            <button type="button" onClick={prova}>Manda una prova</button>
            <button type="button" className="secondario" disabled={occupato} onClick={spegni}>Disattiva qui</button>
          </div>
        </>
      ) : (
        <>
          <p className="notifiche-stato"><MdNotificationsOff size={22} aria-hidden="true" /> {permesso() === 'denied' ? 'Bloccate: riattivale da Impostazioni → Notifiche → Wozzap.' : 'Spente su questo dispositivo'}</p>
          <div className="notifiche-azioni"><button type="button" disabled={occupato} onClick={accendi}>{occupato ? 'Attivo…' : 'Attiva le notifiche'}</button></div>
        </>
      )}
      {avviso && <p role="status" className="notifiche-avviso">{avviso}</p>}
    </section>

    {stato && <section className="notifiche-voci" aria-label="Cosa notificare">
      {VOCI.map(([campo, titolo, spiegazione]) => <label key={campo} className="notifiche-voce">
        <span><strong>{titolo}</strong><small>{spiegazione}</small></span>
        <input type="checkbox" role="switch" checked={!!stato.preferenze[campo]} onChange={e => cambia(campo, e.target.checked)} />
      </label>)}
    </section>}

    {stato && <section className="notifiche-note">
      <p>Chat silenziate: <strong>{stato.chat_silenziate}</strong>. Quelle silenziate su WhatsApp restano silenziate; per cambiare una chat apri la chat, tocca il nome e scegli in «Notifiche».</p>
      {stato.dispositivi.length > 0 && <p>Dispositivi: {stato.dispositivi.map(d => d.dispositivo || 'senza nome').join(', ')}.</p>}
    </section>}
  </div>;
}
